import { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { SchoolData, PolicyDocument } from '@/lib/onboarding-store';
import { holdPolicyFile } from '@/lib/policy-file-store';
import { prettifyPolicyName, policyDisplayName } from '@/lib/policy-naming';
import { WizardStepLayout } from './WizardStepLayout';
import { FileText, Upload, X, Check, AlertTriangle, Shield, ExternalLink, ChevronDown } from 'lucide-react';

interface SchoolStep4Props {
  data: Partial<SchoolData>;
  onChange: (field: keyof SchoolData, value: unknown) => void;
  onNext: () => void;
  onBack: () => void;
  onReturnToReview?: () => void;
}

/** ~1.5 MB. Past this, base64 in localStorage risks the ~5 MB quota, and a
 *  failed setItem corrupts the whole wizard save rather than just this file. */
const MIRROR_LIMIT = 1_500_000;

/**
 * Small files come back as a dataURL for the reload-safety mirror; big ones
 * resolve to '' and rely on the in-memory store. Never rejects — a file we
 * can't read still has its bytes held in memory, so failing the whole batch
 * over one unreadable mirror would be the wrong trade.
 */
function readSmallFileAsDataUrl(file: File): Promise<string> {
  if (file.size > MIRROR_LIMIT) return Promise.resolve('');
  return new Promise<string>((resolve) => {
    const reader = new FileReader();
    reader.onload = (e) => {
      const dataUrl = e.target?.result as string;
      resolve(dataUrl && dataUrl.length < MIRROR_LIMIT ? dataUrl : '');
    };
    reader.onerror = () => resolve('');
    reader.readAsDataURL(file);
  });
}

/** Accepted upload types, shared by the drop zone and the file picker. */
const ACCEPT = '.pdf,.doc,.docx';

export function SchoolStep4Policies({ data, onChange, onNext, onBack, onReturnToReview }: SchoolStep4Props) {
  const policies = data.policies || [];
  const [dragOver, setDragOver] = useState(false);
  const [recentlyUploaded, setRecentlyUploaded] = useState<Set<string>>(new Set());
  /** Filenames rejected because a policy with that name is already in the list. */
  const [duplicates, setDuplicates] = useState<string[]>([]);
  const [reading, setReading] = useState(0);
  const [gdprConsent, setGdprConsent] = useState(false);
  const gdprRef = useRef<HTMLDivElement>(null);
  // The hint's job is finished the moment the agreement is on screen — past
  // that it is pointing at something the user is already looking at.
  const [gdprInView, setGdprInView] = useState(false);

  useEffect(() => {
    const el = gdprRef.current;
    if (!el || typeof IntersectionObserver === 'undefined') return;
    const io = new IntersectionObserver(
      ([entry]) => setGdprInView(entry.isIntersecting),
      { threshold: 0.35 },
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  const removePolicy = (id: string) => {
    onChange('policies', policies.filter(p => p.id !== id));
  };

  /**
   * Take a whole batch of files at once.
   *
   * Every file is read BEFORE anything is written, then committed in a single
   * onChange. The obvious implementation — one onChange per file as its
   * FileReader finishes — loses policies: each callback closes over the
   * `policies` prop as it was when the batch started, so the last write wins
   * and every earlier file in the batch disappears. Dropping eight policies and
   * keeping one is a worse failure than a few tens of milliseconds of "Reading".
   */
  const addFiles = async (incoming: FileList | File[]) => {
    const files = Array.from(incoming);
    if (!files.length) return;

    // The in-memory holding pen is keyed by filename, and so is the post-signup
    // blob upload that drains it — two files called Policy.pdf would collide
    // and one would silently upload twice. Named, not swallowed.
    const taken = new Set(policies.map(p => p.fileName));
    const fresh: File[] = [];
    const clashes: string[] = [];
    for (const f of files) {
      if (taken.has(f.name)) { clashes.push(f.name); continue; }
      taken.add(f.name);
      fresh.push(f);
    }
    setDuplicates(clashes);
    if (!fresh.length) return;

    // Hold the real File synchronously, before any await — localStorage can't
    // take a PDF (~5 MB quota, base64 inflates by a third), so these bytes
    // travel in memory to the post-signup blob upload.
    for (const f of fresh) holdPolicyFile(f.name, f);

    setReading(fresh.length);
    const records: PolicyDocument[] = await Promise.all(fresh.map(async (file, i) => ({
      // Date.now() alone collides across a synchronous batch — every file in one
      // drop would share an id, and React keys plus removePolicy both break.
      id: `${Date.now()}-${i}-${Math.random().toString(36).slice(2, 8)}`,
      // Identity comes from the filename now that there is no type dropdown and
      // no name field. Editable afterwards in Settings › Policies.
      name: prettifyPolicyName(file.name),
      type: '',
      fileName: file.name,
      uploadedAt: new Date().toISOString(),
      // Mirror small files to localStorage so a mid-wizard reload doesn't lose
      // them; large ones would blow the quota and corrupt the whole save, so
      // they stay memory-only.
      fileData: await readSmallFileAsDataUrl(file),
    })));
    setReading(0);

    onChange('policies', [...policies, ...records]);

    const ids = new Set(records.map(r => r.id));
    setRecentlyUploaded(prev => new Set([...prev, ...ids]));
    setTimeout(() => {
      setRecentlyUploaded(prev => {
        const next = new Set(prev);
        for (const id of ids) next.delete(id);
        return next;
      });
    }, 3000);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files?.length) void addFiles(e.dataTransfer.files);
  };

  return (
    <WizardStepLayout
      currentStep={6}
      totalSteps={8}
      title="Policy Documents"
      subtitle="Upload your school policies for cover teachers to review"
      onNext={onNext}
      onBack={onBack}
      nextLabel="Continue to Review"
      validationErrors={!gdprConsent ? ['You must accept the Data Processing Agreement before continuing.'] : []}
      onSubmitAttempt={() => { /* validation is self-evident from checkbox */ }}
      onReturnToReview={onReturnToReview}
    >
      <div className="space-y-6">
        {/* One drop zone for the whole set. Bulk upload is the point: a
            secondary has eight or more policies, and the old flow charged an
            Add → type → name → choose-file round trip for each one. */}
        <div
          onDrop={handleDrop}
          onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
          onDragLeave={(e) => { e.preventDefault(); setDragOver(false); }}
          className={`
            relative p-6 sm:p-8 border-2 border-dashed rounded-2xl text-center transition-all duration-200
            ${dragOver ? 'border-primary bg-primary/5 scale-[1.01]' : 'border-border hover:border-primary/30'}
          `}
        >
          <Upload className="w-8 h-8 mx-auto text-muted-foreground mb-2" />
          <p className="text-sm font-medium text-foreground mb-1">
            {/* Naming the desktop gesture on a phone is a dead instruction. */}
            <span className="hidden sm:inline">Drag &amp; drop all your policies here</span>
            <span className="sm:hidden">Add all your policies at once</span>
          </p>
          <p className="text-xs text-muted-foreground mb-3">
            PDF, DOC or DOCX, select as many as you like
          </p>
          <label className="inline-flex items-center gap-2 px-4 py-2.5 bg-primary/10 text-primary rounded-lg text-sm font-semibold hover:bg-primary/20 transition-colors cursor-pointer">
            <FileText className="w-4 h-4" />
            Choose files
            <input
              type="file"
              accept={ACCEPT}
              multiple
              className="hidden"
              onChange={(e) => {
                if (e.target.files?.length) void addFiles(e.target.files);
                // Reset, or picking the same file twice in a row is a no-op
                // because the input's value hasn't changed.
                e.target.value = '';
              }}
            />
          </label>
          <p className="text-xs text-muted-foreground mt-3">
            We name each one from its file. You can rename or re-file any of them later in Settings.
          </p>
          {/* The policy assistant builds its answers from PDF document blocks —
              that is what carries page citations — and skips anything whose
              magic bytes aren't %PDF (policyChat.ts). Word files still reach
              cover teachers to read; they just can't be searched. Silently
              accepting them and silently not searching them is the one outcome
              worth avoiding, and bulk upload makes it far likelier. */}
          <p className="text-xs text-muted-foreground mt-1.5">
            The policy assistant searches PDFs. Word files are still shared with cover teachers, but it can’t search them.
          </p>

          {reading > 0 && (
            <p className="text-xs font-medium text-primary mt-3">
              Reading {reading} file{reading === 1 ? '' : 's'}…
            </p>
          )}
        </div>

        {/* Filenames already in the list, so nothing is silently dropped. */}
        <AnimatePresence>
          {duplicates.length > 0 && (
            <motion.div
              initial={{ opacity: 0, y: -6 }}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: -6 }}
              className="flex items-start gap-3 p-3 rounded-xl border border-warning/40 bg-warning/5"
            >
              <AlertTriangle className="w-4 h-4 text-warning-text flex-shrink-0 mt-0.5" />
              <div className="text-sm text-foreground min-w-0">
                <span className="font-medium">Already added, so skipped:</span>{' '}
                <span className="text-muted-foreground break-words">{duplicates.join(', ')}</span>
              </div>
              <button
                onClick={() => setDuplicates([])}
                className="p-1 rounded-lg hover:bg-muted transition-colors flex-shrink-0"
                aria-label="Dismiss"
              >
                <X className="w-3.5 h-3.5 text-muted-foreground" />
              </button>
            </motion.div>
          )}
        </AnimatePresence>

        {/* Uploaded list */}
        {policies.length > 0 && (
          <div className="space-y-2">
            <div className="flex items-center justify-between">
              <h3 className="text-sm font-semibold text-foreground">
                {policies.length} polic{policies.length === 1 ? 'y' : 'ies'} added
              </h3>
            </div>
            <AnimatePresence mode="popLayout">
              {policies.map((policy) => (
                <motion.div
                  key={policy.id}
                  initial={{ opacity: 0, y: -8 }}
                  animate={{ opacity: 1, y: 0 }}
                  exit={{ opacity: 0, x: -8 }}
                  layout
                  className={`flex items-center gap-3 p-3 rounded-xl border transition-all duration-500 ${
                    recentlyUploaded.has(policy.id)
                      ? 'bg-success/10 dark:bg-green-950/20 border-success/40'
                      : 'bg-card border-border'
                  }`}
                >
                  <div className="w-9 h-9 rounded-lg bg-success/10 flex items-center justify-center flex-shrink-0">
                    <Check className="w-4 h-4 text-success" />
                  </div>
                  {/* min-w-0 on the chain, or the filename's content width sets
                      the flex minimum and truncate never fires. */}
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-foreground truncate">{policyDisplayName(policy)}</p>
                    <p className="text-xs text-muted-foreground truncate">{policy.fileName}</p>
                  </div>
                  <button
                    onClick={() => removePolicy(policy.id)}
                    className="p-2 hover:bg-destructive/10 rounded-lg transition-colors flex-shrink-0"
                    aria-label={`Remove ${policyDisplayName(policy)}`}
                  >
                    <X className="w-4 h-4 text-destructive" />
                  </button>
                </motion.div>
              ))}
            </AnimatePresence>
          </div>
        )}

        {/* Info box */}
        <div className="flex items-start gap-3 p-4 bg-muted/50 rounded-xl">
          <AlertTriangle className="w-5 h-5 text-accent flex-shrink-0 mt-0.5" />
          <div className="text-sm text-muted-foreground">
            <p>
              <strong className="text-foreground">Why upload policies?</strong>
            </p>
            <p className="mt-1">
              Cover teachers will be shown these policies before accepting assignments,
              ensuring they understand your school's expectations and procedures.
            </p>
            <p className="mt-2 font-medium text-foreground">Suggested policies to upload:</p>
            <ul className="mt-1 space-y-0.5 list-disc list-inside">
              <li>Behaviour policy</li>
              <li>Safeguarding &amp; child protection</li>
              <li>Cover teacher expectations / Cover policy</li>
              <li>Emergency procedures</li>
            </ul>
          </div>
        </div>

        {/* GDPR Consent */}
        <div className="space-y-4 pt-2" ref={gdprRef}>
          <div className="flex items-center gap-2">
            <Shield className="w-5 h-5 text-success" />
            <h3 className="font-semibold text-foreground">Data Protection</h3>
          </div>

          <motion.label
            className={`
              flex items-start gap-4 p-5 rounded-2xl border-2 cursor-pointer transition-all duration-200
              ${gdprConsent
                ? 'border-success bg-success/5'
                : 'border-border hover:border-primary/30'
              }
            `}
          >
            <input
              type="checkbox"
              checked={gdprConsent}
              onChange={(e) => setGdprConsent(e.target.checked)}
              className="mt-1 w-5 h-5 rounded border-border text-success focus:ring-success"
            />
            <div className="flex-1">
              <p className="font-medium text-foreground mb-2">
                I agree to the Terms of Service and Privacy Policy
              </p>
              <p className="text-sm text-muted-foreground mb-3">
                By checking this box, you confirm that:
              </p>
              <ul className="text-sm text-muted-foreground space-y-1.5">
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>You have the authority to register your school</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>You've read and agree to our data processing terms</span>
                </li>
                <li className="flex items-start gap-2">
                  <Check className="w-4 h-4 text-success flex-shrink-0 mt-0.5" />
                  <span>Staff data will be processed in accordance with GDPR</span>
                </li>
              </ul>
              <div className="flex gap-4 mt-4">
                <a
                  href="#"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Privacy Policy
                  <ExternalLink className="w-3 h-3" />
                </a>
                <a
                  href="#"
                  className="inline-flex items-center gap-1 text-sm text-primary hover:underline"
                  onClick={(e) => e.stopPropagation()}
                >
                  Terms of Service
                  <ExternalLink className="w-3 h-3" />
                </a>
              </div>
            </div>
          </motion.label>

          {gdprConsent && (
            <motion.div
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="p-4 bg-success/10 rounded-xl border border-success/20"
            >
              <div className="flex items-center gap-2 text-success font-medium mb-1">
                <Check className="w-5 h-5" />
                Ready to continue
              </div>
              <p className="text-sm text-success/80">
                Click "Continue to Review" to check everything before creating your account.
              </p>
            </motion.div>
          )}
        </div>
      </div>

      {/* Floating scroll-to-required arrow. Centred rather than bottom-right,
          where it collided with the demo inbox button, and retired as soon as
          the agreement scrolls into view. */}
      <AnimatePresence>
        {!gdprConsent && !gdprInView && (
          <motion.button
            key="scroll-hint"
            // x is handed to framer-motion rather than set as a
            // `-translate-x-1/2` class: framer-motion owns `transform`, so a
            // Tailwind translate here would be overwritten by the animated y
            // and the pill would sit half a width off-centre.
            initial={{ opacity: 0, y: 12, x: '-50%' }}
            animate={{ opacity: 1, y: 0, x: '-50%' }}
            exit={{ opacity: 0, y: 12, x: '-50%' }}
            transition={{ duration: 0.25 }}
            onClick={() => gdprRef.current?.scrollIntoView({ behavior: 'smooth', block: 'center' })}
            className="fixed bottom-24 left-1/2 z-20 flex flex-col items-center gap-1.5 group"
            aria-label="Scroll to required field"
          >
            <span className="text-[11px] font-bold text-primary-foreground bg-primary rounded-full px-2.5 py-0.5 shadow-md whitespace-nowrap">
              Required below
            </span>
            <motion.div
              animate={{ y: [0, 6, 0] }}
              transition={{ duration: 1.1, repeat: Infinity, ease: 'easeInOut' }}
              className="w-10 h-10 rounded-full bg-primary shadow-lg flex items-center justify-center"
            >
              <ChevronDown className="w-5 h-5 text-primary-foreground" />
            </motion.div>
          </motion.button>
        )}
      </AnimatePresence>
    </WizardStepLayout>
  );
}
